import React from "react";
import { RelatedLinks } from "../components/site/RelatedLinks";
import { Link } from "react-router-dom";
import { Plane, MapPin, ArrowRight, Users, Luggage, Check } from "lucide-react";
import BookingWidget from "../components/site/BookingWidget";
import { FeatureCard, SectionHead, IconTile } from "../components/site/Common";
import { IMG, POPULAR_ROUTES, FLEET, TESTIMONIALS, vehicleAlt, heroSrcSet, HOME } from "../mock";
import { ExtraBlocks } from "../components/site/SeoExtras";
import { useReveal } from "../lib/lang";
import { ShieldCheck, Star, Clock } from "lucide-react";

const WHY = [
  ["compass", "Local Corfu Knowledge", "Drivers who know every road, shortcut and hidden corner of the island."],
  ["shield", "Professional Drivers", "Courteous, licensed, English-speaking drivers in spotless vehicles."],
  ["car", "Private Door-to-Door", "Straight from your arrival point to your exact destination — no sharing."],
  ["plane", "Flight Monitoring", "We track your flight and adjust for delays at no extra cost."],
  ["star", "Comfortable Vehicles", "Modern, air-conditioned Mercedes-class cars, SUVs and vans."],
  ["clock", "24/7 Customer Support", "Real people on call around the clock, whenever you land."],
];

const SERVICES = [
  ["plane", "Airport Transfers", "Meet & greet, flight tracking, door-to-door.", "/airport-transfers"],
  ["anchor", "Port Transfers", "Cruise, ferry & marina pickups with assistance.", "/transfers/port"],
  ["car", "Private Transfers", "Island-wide transfers between any two points.", "/transfers/private"],
  ["clock", "Hourly Chauffeur", "Your driver, your schedule — by the hour.", "/chauffeur"],
];

export const VehicleCard = ({ v, priceLabel = "Price on request", testid }) => (
  <div className="reveal lift bg-white rounded-2xl overflow-hidden text-[#202522]" data-testid={testid}>
    <div className="relative h-[170px] sm:h-[245px] bg-[#F1ECE0] overflow-hidden group">
      <img src={v.image} loading="lazy" alt={vehicleAlt(v)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <span className="absolute top-3 left-3 rounded-full bg-[#183C35] text-[#F7F3EA] text-[12.5px] font-medium px-3 py-1">{v.name}</span>
    </div>
    <div className="p-4 sm:p-5">
      <p className="text-[13px] text-[#202522]/65">{v.model}</p>
      <div className="flex items-center gap-5 mt-3 text-[15px]">
        <span className="flex items-center gap-2"><Users className="w-4 h-4 text-[#3E7F8E]" />{v.pax}</span>
        <span className="flex items-center gap-2"><Luggage className="w-4 h-4 text-[#3E7F8E]" />{v.bags}</span>
      </div>
      <ul className="mt-3 sm:mt-4 space-y-1.5 sm:space-y-2">
        {v.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-[13.5px] sm:text-[15px] text-[#202522]/85"><Check className="w-4 h-4 mt-0.5 text-[#3E7F8E]" />{f}</li>
        ))}
      </ul>
      <div className="border-t border-[#183C35]/10 mt-5 pt-4 text-[15px] text-[#202522]/70">{priceLabel}</div>
    </div>
  </div>
);

const Home = () => {
  useReveal();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img src={IMG.hero} srcSet={heroSrcSet(IMG.hero)} sizes="100vw" fetchPriority="high" alt={HOME.heroAlt} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#183C35]/45" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#183C35]/55 via-[#183C35]/20 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-8 sm:py-16 lg:py-[110px] grid lg:grid-cols-[1fr_572px] gap-5 sm:gap-12 items-center">
          <div className="fade-up">
            <h1 className="text-[10.5px] sm:text-[13px] tracking-[0.25em] sm:tracking-[0.3em] uppercase text-[#F7F3EA]/85 font-normal">Aloha Corfu Transfers · Private Airport &amp; Port Transfers in Corfu</h1>
            <p role="heading" aria-level="2" className="font-serif-d font-bold text-[30px] sm:text-[60px] leading-[1.08] sm:leading-[1.03] mt-2 sm:mt-5 text-[#F7F3EA]">
              {HOME.heroTitle1}<br /><span className="text-[#E3CBA5]">{HOME.heroTitle2}</span>
            </p>
            <p className="hidden sm:block mt-6 text-[19px] leading-[1.5] text-[#F7F3EA]/85 max-w-[500px]">
              {HOME.heroSub}
            </p>
            <div className="flex flex-wrap gap-x-3 sm:gap-x-5 gap-y-1 sm:gap-y-2 mt-2.5 sm:mt-7 text-[11.5px] sm:text-[15px] text-[#F7F3EA]/80 [&_svg]:w-3 [&_svg]:h-3 sm:[&_svg]:w-4 sm:[&_svg]:h-4">
              <span className="flex items-center gap-1.5 sm:gap-2"><ShieldCheck className="w-4 h-4" />Flight monitoring</span>
              <span className="flex items-center gap-1.5 sm:gap-2"><Star className="w-4 h-4" />Fixed prices</span>
              <span className="flex items-center gap-1.5 sm:gap-2"><Clock className="w-4 h-4" />24/7 support</span>
            </div>
          </div>
          <div className="fade-up" style={{ animationDelay: ".15s" }}>
            <BookingWidget />
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="py-12 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead eyebrow="Our promise" title="Why Choose Aloha Corfu Transfers?" sub="The small details that make a long travel day feel easy." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 mt-6 sm:mt-12">
            {WHY.map(([i, t, d]) => <FeatureCard key={t} icon={i} title={t} text={d} testid="why-card" />)}
          </div>
        </div>
      </section>

      {/* ROUTES */}
      <section className="py-12 sm:py-24 bg-white border-y border-[#183C35]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead eyebrow="Popular transfers" title="Corfu Airport Taxi Routes & Fixed Fares" sub="Transparent, fixed prices from Corfu Airport. Tap a route to start your booking." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-4 mt-6 sm:mt-12">
            {POPULAR_ROUTES.map((r) => (
              <Link key={r.to} data-testid="route-card"
                to={`/book?type=oneway&pickup=${encodeURIComponent("Corfu International Airport (CFU)")}&dropoff=${encodeURIComponent(r.dest)}&pax=2&bags=2`}
                className="reveal group flex items-center sm:items-start justify-between rounded-2xl border border-[#183C35]/10 bg-[#F6F2E9] px-4 py-3 sm:p-5 hover:border-[#183C35]/30 hover:bg-[#F1EBDD] hover:shadow-[0_14px_30px_-18px_rgba(24,60,53,.4)] transition-[border-color,background-color,box-shadow] duration-300">
                <div>
                  <p className="flex items-center gap-2 text-[13px] text-[#202522]/65"><Plane className="w-3.5 h-3.5 text-[#3E7F8E]" />Corfu Airport</p>
                  <p className="flex items-center gap-2 mt-1 sm:mt-2 font-serif-d text-[18px] sm:text-[21px] text-[#183C35]"><MapPin className="w-4 h-4 text-[#3E7F8E]" />{r.to}</p>
                </div>
                <div className="text-right">
                  <p className="text-[12px] text-[#202522]/60">from</p>
                  <p className="font-serif-d text-[21px] sm:text-[24px] text-[#183C35] leading-tight">€{r.price}</p>
                  <ArrowRight className="w-4 h-4 ml-auto mt-1 text-[#3E7F8E] transition-transform duration-300 group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>
          <p className="mt-5 text-[13px] text-[#202522]/60">Sample prices for the most affordable available vehicle. Final price depends on vehicle and options.</p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-12 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead center eyebrow="What we do" title="Private Airport & Port Transfers in Corfu" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mt-6 sm:mt-12">
            {SERVICES.map(([i, t, d, h]) => (
              <Link key={t} to={h} className="reveal lift group bg-white rounded-2xl border border-[#183C35]/10 p-4 sm:p-6" data-testid="service-card">
                <IconTile name={i} dark />
                <h3 className="mt-3 sm:mt-5 text-[15px] sm:text-[19px] font-medium text-[#183C35] leading-snug">{t}</h3>
                <p className="mt-1.5 sm:mt-2.5 text-[13px] sm:text-[15px] leading-relaxed text-[#202522]/70">{d}</p>
                <span className="mt-3 sm:mt-5 text-[13.5px] sm:text-[15px] inline-flex items-center gap-1.5 text-[15px] text-[#3E7F8E] font-medium">Learn more <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <RelatedLinks current="/" />

      {/* FLEET */}
      <section className="py-12 sm:py-24 bg-[#1A3C37] text-[#F7F3EA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="reveal flex flex-col sm:flex-row sm:items-end justify-between gap-6">
            <div>
              <p className="eyebrow !text-[#7FB8C4]">The fleet</p>
              <h2 className="font-serif-d text-[26px] sm:text-[40px] font-semibold mt-2 sm:mt-3">Corfu Private Transfer Fleet</h2>
              <p className="mt-2 sm:mt-3 text-[15px] sm:text-[17px] text-[#F7F3EA]/75">Immaculately kept Mercedes-class sedans, SUVs and vans for every group size.</p>
            </div>
            <Link to="/fleet" data-testid="view-fleet-btn" className="self-start sm:self-auto inline-flex items-center gap-2 rounded-full border border-[#F7F3EA]/30 px-5 py-2.5 text-[15px] hover:bg-[#F7F3EA] hover:text-[#183C35] transition-colors duration-200">
              View our fleet <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="flex md:grid md:grid-cols-3 gap-4 sm:gap-5 mt-6 sm:mt-12 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-4 px-4 md:mx-0 md:px-0 pb-2 md:pb-0 [scrollbar-width:none]">
            {FLEET.map((v) => <div key={v.id} className="min-w-[82%] sm:min-w-[60%] md:min-w-0 snap-start"><VehicleCard v={v} testid="home-vehicle-card" /></div>)}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-12 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <SectionHead center eyebrow="Guest stories" title={HOME.reviewsTitle} />
          <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mt-6 sm:mt-12 overflow-x-auto sm:overflow-visible snap-x snap-mandatory -mx-4 px-4 sm:mx-0 sm:px-0 pb-2 sm:pb-0 [scrollbar-width:none]">
            {TESTIMONIALS.map((t) => (
              <figure key={t.name} className="reveal lift min-w-[80%] sm:min-w-0 snap-start rounded-2xl bg-[#F6F2E9] border border-[#183C35]/10 p-5 sm:p-6 flex flex-col" data-testid="testimonial-card">
                <div className="flex gap-0.5 text-[#C9A46A]">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}</div>
                <blockquote className="mt-3 sm:mt-4 font-serif-d text-[16px] sm:text-[18px] leading-relaxed text-[#183C35] flex-1">"{t.quote}"</blockquote>
                <figcaption className="mt-5 text-[14px] text-[#202522]/65">{t.name} · {t.country}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 empty:hidden"><ExtraBlocks page="home" /></div>

      {/* CTA */}
      <section className="py-12 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="reveal rounded-[22px] sm:rounded-[28px] bg-[#1A3C37] px-5 py-10 sm:px-6 sm:py-16 text-center relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#E3CBA5]/10 blur-2xl" />
            <h2 className="font-serif-d text-[26px] sm:text-[44px] font-semibold text-[#F7F3EA] relative">{HOME.ctaTitle}</h2>
            <p className="mt-2 sm:mt-3 text-[15px] sm:text-[17px] text-[#F7F3EA]/80 relative">{HOME.ctaSub}</p>
            <Link to="/book" data-testid="cta-book-now" className="relative mt-6 sm:mt-8 inline-flex items-center gap-2 rounded-full bg-[#E3CBA5] text-[#183C35] font-semibold px-7 py-3.5 hover:bg-[#EBD8B8] transition-colors duration-200">
              Book Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
